/**
 * lingue.ts — I TRE ASSI dell'internazionalizzazione, tenuti separati apposta.
 *
 * ## Perché tre assi e non uno
 * L'errore che si fa sempre è trattare "la lingua" come se decidesse tutto. Non è così,
 * e il controesempio è banale: un'azienda di Zurigo parla TEDESCO, sta in SVIZZERA e
 * incassa in FRANCHI. Tre valori indipendenti. Se li si lega, il giorno che arriva quel
 * cliente si riscrive il modello dati invece di compilare tre campi.
 *
 *   LINGUA  → in che lingua leggo l'interfaccia          (it, en, ...)
 *   PAESE   → quali regole fiscali e formati mi valgono  (IT, DE, CH, ...)
 *   VALUTA  → in cosa incasso                            (EUR, CHF, USD, ...)
 *
 * Il fuso orario è un quarto asse, e sta col PAESE (non con la lingua): un tedesco che
 * usa l'interfaccia in inglese vuole comunque gli appuntamenti su Berlino.
 *
 * ## Stato attuale (28/08/2026)
 * Si spedisce con `it` e `en`. Le altre lingue si aggiungono SOLO qui e in `locales/`:
 * nessun altro file del progetto deve elencare le lingue. Se per aggiungere lo spagnolo
 * bisogna toccare più di due punti, l'architettura è sbagliata e va corretta qui.
 *
 * ⚠️ NON aggiungere `LINGUE` a mano altrove. Se serve un elenco, si importa da qui.
 */

// ============================================================================
// LINGUA
// ============================================================================

/**
 * Lingue in cui l'interfaccia è realmente disponibile.
 * `zz` NON è una lingua vera: è la pseudo-lingua di collaudo (vedi `pseudo.ts`),
 * accesa solo in sviluppo. Sta qui perché i18next la deve conoscere come le altre.
 */
export const LINGUE = ['it', 'en'] as const;
export type Lingua = (typeof LINGUE)[number];

/** Codice della pseudo-lingua di collaudo. Mai spedita a un cliente. */
export const LINGUA_PSEUDO = 'zz' as const;

/**
 * Lingua sorgente: quella in cui le frasi vengono SCRITTE dagli sviluppatori.
 * È anche la lingua di ripiego quando una traduzione manca.
 * ⚠️ Non cambiarla senza rigenerare tutti i file di traduzione: le chiavi restano,
 * ma il testo di ripiego cambierebbe lingua sotto i piedi agli utenti.
 */
export const LINGUA_BASE: Lingua = 'it';

/** Etichetta di ogni lingua NELLA LINGUA STESSA (mai tradotta: "Deutsch", non "Tedesco"). */
export const NOME_LINGUA: Record<Lingua, string> = {
  it: 'Italiano',
  en: 'English',
};

/** Vero se la stringa è una lingua supportata. Da usare al posto dei cast. */
export function isLingua(v: unknown): v is Lingua {
  return typeof v === 'string' && (LINGUE as readonly string[]).includes(v);
}

// ============================================================================
// PAESE
// ============================================================================

/**
 * Paesi per cui esiste (o esisterà) un comportamento fiscale/normativo dedicato.
 * ⚠️ Questo elenco NON è "dove possiamo vendere": è "dove sappiamo fare le regole".
 * Un tenant può stare in un paese non elencato: userà i valori neutri.
 * Codici ISO 3166-1 alpha-2.
 */
export const PAESI_SUPPORTATI = ['IT'] as const;
export type PaeseSupportato = (typeof PAESI_SUPPORTATI)[number];

/** Paese di un tenant: uno di quelli supportati oppure un qualsiasi ISO a 2 lettere. */
export type Paese = string;

/** Paese di ripiego per i tenant creati prima che il campo esistesse. */
export const PAESE_DEFAULT: Paese = 'IT';

/**
 * Impostazioni che dipendono dal PAESE, non dalla lingua.
 * Nota: `localeFormati` è il locale usato per formattare numeri e date, ed è una cosa
 * diversa dalla lingua dell'interfaccia. Un italiano che legge l'inglese si aspetta
 * comunque "1.234,56", non "1,234.56".
 */
export interface ImpostazioniPaese {
  /** Locale BCP-47 per numeri, date e ordinamento. Es. 'it-IT'. */
  readonly localeFormati: string;
  /** Fuso orario IANA. Es. 'Europe/Rome'. */
  readonly fusoOrario: string;
  /** Valuta predefinita (ISO 4217). Il tenant può sovrascriverla. */
  readonly valutaDefault: Valuta;
  /** Primo giorno della settimana: 0 = domenica, 1 = lunedì. */
  readonly primoGiornoSettimana: 0 | 1;
}

export const IMPOSTAZIONI_PAESE: Record<PaeseSupportato, ImpostazioniPaese> = {
  IT: {
    localeFormati: 'it-IT',
    fusoOrario: 'Europe/Rome',
    valutaDefault: 'EUR',
    primoGiornoSettimana: 1,
  },
};

/** Valori neutri, usati solo se perfino il codice paese è illeggibile. */
export const IMPOSTAZIONI_PAESE_NEUTRE: ImpostazioniPaese = {
  localeFormati: 'en-GB',
  fusoOrario: 'UTC',
  valutaDefault: 'EUR',
  primoGiornoSettimana: 1,
};

/**
 * Locale di formattazione per un paese che NON abbiamo modellato.
 *
 * Un test ha scoperto perché serve: con un ripiego fisso su `en-GB`, un tenant
 * statunitense avrebbe visto le date in ordine britannico (28/08 invece di 08/28) e gli
 * elenchi senza la virgola di Oxford. Sbagliato, e per giunta invisibile.
 *
 * Qui non si indovina: si chiede a `Intl` qual è la lingua più probabile di quel paese
 * (`und-US` → `en`) e si costruisce il locale. Non è una scelta di prodotto, è solo il
 * modo giusto di scrivere una data quando non ci hanno detto altro.
 * ⚠️ Il FUSO ORARIO invece non si deduce: molti paesi ne hanno più d'uno. Resta UTC
 * finché il tenant non lo dichiara, e UTC è visibilmente sbagliato invece che
 * subdolamente sbagliato.
 */
function localeProbabileDi(paese: string): string {
  try {
    const massimizzato = new Intl.Locale(`und-${paese}`).maximize();
    return `${massimizzato.language}-${paese}`;
  } catch {
    return IMPOSTAZIONI_PAESE_NEUTRE.localeFormati;
  }
}

export function impostazioniDiPaese(paese: Paese | undefined | null): ImpostazioniPaese {
  const p = (paese || PAESE_DEFAULT).toUpperCase();
  const modellato = (IMPOSTAZIONI_PAESE as Record<string, ImpostazioniPaese>)[p];
  if (modellato) return modellato;
  if (!/^[A-Z]{2}$/.test(p)) return IMPOSTAZIONI_PAESE_NEUTRE;
  return { ...IMPOSTAZIONI_PAESE_NEUTRE, localeFormati: localeProbabileDi(p) };
}

// ============================================================================
// VALUTA
// ============================================================================

/**
 * Valute gestite. L'elenco serve a evitare i refusi, non a limitare il mercato:
 * `Intl` sa formattare qualunque codice ISO 4217 valido.
 */
export const VALUTE = ['EUR', 'CHF', 'GBP', 'USD'] as const;
export type Valuta = (typeof VALUTE)[number];

export const VALUTA_DEFAULT: Valuta = 'EUR';

export function isValuta(v: unknown): v is Valuta {
  return typeof v === 'string' && (VALUTE as readonly string[]).includes(v);
}

// ============================================================================
// CONTESTO COMPLETO
// ============================================================================

/**
 * Tutto ciò che serve per formattare e tradurre correttamente per un utente.
 * Si costruisce UNA volta (dal tenant + preferenza utente) e si passa in giro:
 * nessuna funzione di formattazione deve andarsi a leggere il tenant da sola.
 */
export interface ContestoLocale {
  readonly lingua: Lingua | typeof LINGUA_PSEUDO;
  readonly paese: Paese;
  readonly valuta: Valuta;
  readonly localeFormati: string;
  readonly fusoOrario: string;
}

/**
 * Costruisce il contesto a partire dai dati del tenant e dalla scelta dell'utente.
 * Ogni campo assente ricade su un valore sensato: un tenant creato nel 2024, che non ha
 * nessuno di questi campi, continua a comportarsi ESATTAMENTE come prima.
 */
export function creaContestoLocale(input: {
  lingua?: string | null;
  paese?: string | null;
  valuta?: string | null;
  fusoOrario?: string | null;
}): ContestoLocale {
  const paese = (input.paese || PAESE_DEFAULT).toUpperCase();
  const base = impostazioniDiPaese(paese);
  const lingua = isLingua(input.lingua)
    ? input.lingua
    : input.lingua === LINGUA_PSEUDO
      ? LINGUA_PSEUDO
      : LINGUA_BASE;

  return {
    lingua,
    paese,
    valuta: isValuta(input.valuta) ? input.valuta : base.valutaDefault,
    localeFormati: base.localeFormati,
    fusoOrario: input.fusoOrario || base.fusoOrario,
  };
}

/** Contesto di ripiego: l'Italia di oggi. Usato prima che il tenant sia caricato. */
export const CONTESTO_ITALIA: ContestoLocale = creaContestoLocale({});

// ============================================================================
// LA LINGUA DEI DATI — aggiunta il 01/09/2026 per `testoMultilingua.ts`
//
// Nulla sopra questa riga e' stato modificato: sono SOLO aggiunte.
// Serve perche' un DATO scritto da una persona parla un alfabeto piu' largo di quello
// che l'INTERFACCIA sa mostrare, e i due non vanno confusi (il perche' e' qui sotto).
// ============================================================================


/**
 * Un codice di lingua COME PUO' ARRIVARE DAI DATI.
 *
 * ## Perche' il DATO parla un alfabeto piu' largo del CODICE
 * `Lingua` e' l'elenco che QUESTA versione dell'app sa mostrare. Un documento su Firestore
 * puo' contenere una lingua che questa versione non conosce ancora: l'ha scritta una versione
 * piu' nuova, uno script, o l'Admin SDK. Se la lettura la «riportasse» a una lingua conosciuta
 * farebbe due danni in fila: mostrerebbe un testo tedesco spacciandolo per italiano, e la
 * scrittura successiva lo sovrascriverebbe. Quindi: il tipo del DATO e' largo, il tipo di cio'
 * che si SCRIVE resta stretto.
 */
export type CodiceLingua = string;

/**
 * La lingua con cui si LEGGONO I DATI, partendo da qualunque cosa arrivi.
 *
 * Tre casi, tutti gia' visti nel gestionale:
 *  - `'it-IT'` — i18next ha `load: 'languageOnly'`, quindi serve le risorse `it` e nessuno si
 *    accorge che `i18n.language` e' regionale. Usato come chiave di una mappa non trova niente,
 *    e un italiano si vedrebbe il segno di ripiego sul proprio testo.
 *  - `'zz'` — la pseudo-lingua di collaudo vale per le FRASI DEL CODICE, non per i dati: un
 *    dato non ha una versione pseudo, quindi si legge nella lingua base.
 *  - qualunque altra cosa (una preferenza sporca, `undefined`) — lingua base.
 */
export function normalizzaLingua(v: unknown): Lingua {
  if (typeof v !== 'string') return LINGUA_BASE;
  const solaLingua = v.trim().toLowerCase().split(/[-_]/)[0];
  return isLingua(solaLingua) ? solaLingua : LINGUA_BASE;
}

/**
 * Vero se la chiave puo' essere una lingua, IN LETTURA.
 *
 * ⚠️ Deliberatamente piu' largo di `isLingua`: `pt-BR` e `zh-Hant` sono lingue vere, e un
 * predicato stretto qui le farebbe sparire da `risolviTesto` lasciandole sul disco — cioe' il
 * dato invisibile, che e' il guasto peggiore perche' invita a ricreare il documento.
 * In SCRITTURA vale invece `isLingua`: solo `it`/`en`, che non contengono il trattino e quindi
 * non rompono il percorso puntato di Firestore. Il giorno che una variante regionale entra in
 * `LINGUE`, la patch dovra' passare da `FieldPath`.
 */
export function eChiaveLinguaLeggibile(v: unknown): v is CodiceLingua {
  return typeof v === 'string' && /^[a-z]{2,3}(-[a-z0-9]{2,8})*$/i.test(v);
}

/** Il nome di una lingua, anche di una che questa versione non conosce (allora: «DE»). */
export function nomeLingua(codice: CodiceLingua): string {
  return isLingua(codice) ? NOME_LINGUA[codice] : codice.toUpperCase();
}

/**
 * La lingua con cui si leggono i DATI di questo utente.
 * `ContestoLocale.lingua` puo' valere `'zz'`. Questa funzione esiste perche' nessuno metta un
 * cast — il cast e' cio' che poi fa passare anche `'it-IT'`.
 */
export function linguaDeiDati(contesto: ContestoLocale): Lingua {
  return isLingua(contesto.lingua) ? contesto.lingua : LINGUA_BASE;
}


